import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectSample, Sample, UpdateSamplePayload } from '../../@types/samples/types';
import { showSuccess } from '../../components/utils/notifications';
import { Well } from '../../@types/wells/types';
import { SortState } from '../../design-system';
import { usePersistentState } from '../../hooks/usePersistentState';
import {
  analysisSignature,
  buildUpdatePayload,
  buildWellNameMap,
  computeAverageRow,
  sortRows,
  toSummaryRow,
} from './helpers';
import { EditableField, GroupMode, SummaryRow } from './types';

export interface SummaryGroup {
  key: string;
  label: string;
  rows: SummaryRow[]; // includes the trailing average row
}

interface UseSamplesSummaryArgs {
  samples: ProjectSample[];
  wells: Well[];
  /** Persists a minimal payload; resolves with the updated sample. */
  onSave: (payload: UpdateSamplePayload) => Promise<Sample>;
  /** Called after a successful save so the container can patch its data store. */
  onSaved?: (updated: Sample) => void;
}

export const useSamplesSummary = ({
  samples,
  wells,
  onSave,
  onSaved,
}: UseSamplesSummaryArgs) => {
  const [search, setSearch] = useState('');
  // View preferences persist across reloads/revisits (see usePersistentState).
  const [wellFilter, setWellFilter] = usePersistentState<string>(
    'samplesSummary.wellFilter',
    'all'
  );
  const [groupMode, setGroupMode] = usePersistentState<GroupMode>(
    'samplesSummary.groupMode',
    'none'
  );
  const [sort, setSort] = useState<SortState | null>(null);

  // A persisted well may belong to another project; fall back to "all" once the
  // current project's wells are loaded and none matches.
  useEffect(() => {
    if (
      wellFilter !== 'all' &&
      wells.length > 0 &&
      !wells.some(well => well.uid === wellFilter)
    ) {
      setWellFilter('all');
    }
  }, [wells, wellFilter, setWellFilter]);

  const [savingUid, setSavingUid] = useState<string | null>(null);
  /**
   * Errors only. A save confirmation is transient -- it goes to `showSuccess`,
   * because "it worked" stops being useful the moment after it happens, whereas
   * a failure is something the user still has to deal with.
   */
  const [banner, setBanner] = useState<{ type: 'error'; message: string } | null>(null);

  const wellNameByUid = useMemo(() => buildWellNameMap(wells), [wells]);

  const sampleByUid = useMemo(() => {
    const map: Record<string, ProjectSample> = {};
    samples.forEach(sample => {
      map[sample.uid] = sample;
    });
    return map;
  }, [samples]);

  const allRows = useMemo(
    () => samples.map(sample => toSummaryRow(sample, wellNameByUid)),
    [samples, wellNameByUid],
  );

  const wellOptions = useMemo(
    () => [
      { value: 'all', label: 'All wells' },
      ...wells.map(well => ({ value: well.uid, label: well.well_name })),
    ],
    [wells],
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter(row => {
      if (wellFilter !== 'all' && row.well_uid !== wellFilter) return false;
      if (term && !row.sample_code.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [allRows, search, wellFilter]);

  const groups = useMemo<SummaryGroup[]>(() => {
    const orderRows = (rows: SummaryRow[]): SummaryRow[] => {
      const ordered = sort ? sortRows(rows, sort.key as keyof SummaryRow, sort.direction) : rows;
      if (ordered.length === 0) return ordered;
      return [...ordered, computeAverageRow(ordered)];
    };

    if (groupMode === 'none') {
      return [{ key: 'all', label: '', rows: orderRows(filteredRows) }];
    }

    // Compute a composite group key + label per row based on the active dimensions.
    const keyOf = (row: SummaryRow): { key: string; label: string } => {
      const sig = analysisSignature(row.analyses);
      switch (groupMode) {
        case 'well':
          return { key: row.well_uid, label: row.wellName };
        case 'analysis':
          return { key: sig, label: sig };
        case 'well-analysis':
        default:
          return { key: `${row.well_uid}||${sig}`, label: `${row.wellName} · ${sig}` };
      }
    };

    const byKey = new Map<string, { label: string; rows: SummaryRow[] }>();
    filteredRows.forEach(row => {
      const { key, label } = keyOf(row);
      const entry = byKey.get(key) ?? { label, rows: [] };
      entry.rows.push(row);
      byKey.set(key, entry);
    });

    return Array.from(byKey.entries())
      .map(([key, { label, rows }]) => ({ key, label, rows: orderRows(rows) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredRows, groupMode, sort]);

  const persist = useCallback(
    async (
      original: ProjectSample,
      fieldEdits: Partial<Record<EditableField, string>>,
    ): Promise<{ ok: boolean; fieldErrors: Partial<Record<EditableField, string>> }> => {
      const { payload, errors: fieldErrors } = buildUpdatePayload(original, fieldEdits);

      if (Object.keys(fieldErrors).length > 0) {
        setBanner({
          type: 'error',
          message: Object.values(fieldErrors)[0] ?? 'Invalid sample values',
        });
        return { ok: false, fieldErrors };
      }
      if (!payload) {
        return { ok: true, fieldErrors: {} }; // nothing changed
      }

      setSavingUid(original.uid);
      setBanner(null);
      try {
        const updated = await onSave(payload);
        onSaved?.(updated);
        showSuccess(`Saved ${original.sample_code}`);
        return { ok: true, fieldErrors: {} };
      } catch (error) {
        const message = (error as { message?: string })?.message ?? 'Failed to save sample';
        setBanner({ type: 'error', message });
        return { ok: false, fieldErrors: {} };
      } finally {
        setSavingUid(null);
      }
    },
    [onSave, onSaved],
  );

  const dismissBanner = useCallback(() => setBanner(null), []);

  return {
    // filter / sort / group
    search,
    setSearch,
    wellFilter,
    setWellFilter,
    groupMode,
    setGroupMode,
    sort,
    setSort,
    wellOptions,
    groups,
    totalCount: allRows.length,
    filteredCount: filteredRows.length,
    // inline editing
    commitRow: persist,
    savingUid,
    sampleByUid,
    // feedback
    banner,
    dismissBanner,
  };
};
